import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { RepPrompt } from './RepPrompt';
import { snakDisclaimer, snakMeta } from '../../data/snak';
import type { RepPromptContent } from '../../types/snak';
interface IpadFrameProps {
  step: number;
  total: number;
  chapter: string;
  children: React.ReactNode;
  prompt?: RepPromptContent;
  promptsVisible: boolean;
  onTogglePrompts: () => void;
  onOpenStudy: () => void;
  footnote?: string;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  /** Quiet escape hatch, e.g. skipping an unanswered question. */
  secondary?: {
    label: string;
    onClick: () => void;
  };
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function IpadFrame({
  step,
  total,
  chapter,
  children,
  prompt,
  promptsVisible,
  onTogglePrompts,
  onOpenStudy,
  footnote,
  onBack,
  onNext,
  nextLabel = 'Continue',
  nextDisabled = false,
  secondary
}: IpadFrameProps) {
  const showPrompt = Boolean(prompt) && promptsVisible;
  const progress = (step + 1) / total;
  return <div className="flex min-h-full w-full items-center justify-center bg-mist px-6 py-10" data-id="element-57" data-mp="sx82pfq4cvo">
      <div className="w-full max-w-[1080px]" data-id="element-58" data-mp="ekam5nuv4ijb">
        <div className="rounded-[38px] bg-navy p-2.5 shadow-soft" data-id="element-59" data-mp="el3l42nky3qw">
          <div className="flex aspect-[4/3] w-full flex-col overflow-hidden rounded-[28px] bg-paper" data-id="element-60" data-mp="9rr002i40f2w">
            <div className="relative shrink-0 border-b border-line" data-id="element-61" data-mp="ofvh521ikijk">
              <div className="absolute inset-x-0 top-0 h-[3px] bg-line/70" data-id="element-62" data-mp="jj51ugxj9qz7">
                <motion.div animate={{
                scaleX: progress
              }} initial={false} transition={{
                duration: 0.28,
                ease: EASE
              }} style={{
                transformOrigin: 'left'
              }} className="h-full bg-blue" data-id="element-63" data-mp="xhv1a37ts2qq" />
              </div>

              <div className="flex items-center justify-between gap-6 px-8 pb-4 pt-5" data-id="element-64" data-mp="2v8efjr4yuhq7">
                <div className="flex items-baseline gap-3" data-id="element-65" data-mp="y7ibf4c4wuk3">
                  <span className="font-display text-[13px] font-bold tabular-nums text-blue" data-id="element-66" data-mp="y935ksujebhf">
                    {String(step + 1).padStart(2, '0')}
                  </span>
                  <span className="text-[11px] tabular-nums text-mut" data-id="element-67" data-mp="184n0pd1a1654">
                    / {String(total).padStart(2, '0')}
                  </span>
                  <span className="ml-1 text-[10px] uppercase tracking-micro text-navy" data-id="element-68" data-mp="1h64f8qz1czrp">
                    {chapter}
                  </span>
                  <span className="hidden text-[10px] uppercase tracking-micro text-mut lg:inline" data-id="element-69" data-mp="1m45zhf87754r">
                    · {snakMeta.congress}
                  </span>
                </div>

                <div className="flex items-center gap-2" data-id="element-70" data-mp="1vvae2dtyonz2">
                  <button type="button" onClick={onTogglePrompts} aria-pressed={promptsVisible} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10.5px] font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue ${promptsVisible ? 'border-navy bg-navy text-paper' : 'border-line bg-paper text-mut hover:border-mut hover:text-navy'}`} data-id="element-71" data-mp="1vwv904k6u8xg">
                    {promptsVisible ? <EyeIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-72" data-mp="cvb1lacbwdkm" /> : <EyeOffIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-73" data-mp="7yax33v4nivm" />}
                    Rep prompts
                  </button>
                  <button type="button" onClick={onOpenStudy} className="rounded-full border border-blue/60 bg-paper px-3 py-1 text-[10.5px] font-medium text-navy transition-colors duration-150 hover:border-blue hover:bg-sky/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-74" data-mp="3ucuaoyca2u11">
                    Study design
                  </button>
                </div>
              </div>
            </div>

            <div className="relative flex flex-1 flex-col overflow-hidden" data-id="element-75" data-mp="fdcvvbjneumn">{children}</div>

            <div className="shrink-0 border-t border-line bg-paper px-8 py-4" data-id="element-76" data-mp="1i0x8t2dgk0g">
              <div className="flex items-center justify-between gap-8" data-id="element-77" data-mp="6fj8iq3v3bbm">
                {showPrompt && prompt ? <RepPrompt prompt={prompt} data-id="element-78" data-mp="u6op57hmm7mz" /> : <p className="max-w-xl flex-1 text-[10px] leading-relaxed text-mut" data-id="element-79" data-mp="z2dkeymm52dh">
                    {footnote ?? snakMeta.principle}
                  </p>}

                {(onBack || onNext) && <div className="flex shrink-0 items-center gap-2" data-id="element-80" data-mp="pbsltb6xvxuk">
                    {secondary && <button type="button" onClick={secondary.onClick} className="px-3 text-[12px] font-medium text-mut underline-offset-4 transition-colors duration-150 hover:text-navy hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-81" data-mp="1d1mxvqjfpidr">
                        {secondary.label}
                      </button>}
                    {onBack && <button type="button" onClick={onBack} aria-label="Previous page" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-line text-mut transition-colors duration-150 hover:border-mut hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-82" data-mp="37dekzfys63wu">
                        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-83" data-mp="7vnaxw6ycdag" />
                      </button>}
                    {onNext && <button type="button" onClick={onNext} disabled={nextDisabled} className="group flex h-12 min-w-[220px] items-center justify-between gap-3 rounded-full bg-navy pl-6 pr-2 text-[13px] font-medium text-paper transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 disabled:opacity-30" data-id="element-84" data-mp="2zg3u02tkvqx">
                        {nextLabel}
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-mint/20" data-id="element-85" data-mp="2i176swfse1pg">
                          <ArrowRightIcon className="h-4 w-4 text-mint transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-86" data-mp="2mygsc1021t80" />
                        </span>
                      </button>}
                  </div>}
              </div>
            </div>
          </div>
        </div>

        <p className="mx-auto mt-5 max-w-3xl text-center text-[10.5px] leading-relaxed text-mut" data-id="element-87" data-mp="1rpdikpof1yfl">
          {snakDisclaimer}
        </p>
      </div>
    </div>;
}